import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Play } from 'lucide-react';
import { getMovie, type MovieDetail as MovieDetailType } from '../api/library';
import { getPlaybackUrl, type PlaybackUrl } from '../api/playback';
import { getProgress } from '../api/progress';
import { HlsOdenPlayer } from '../components/HlsOdenPlayer';

export function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const [movie, setMovie] = useState<MovieDetailType | null>(null);
  const [playback, setPlayback] = useState<PlaybackUrl | null>(null);
  const [resumeFrom, setResumeFrom] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    Promise.all([
      getMovie(id),
      getProgress(id).catch(() => null),
    ])
      .then(([m, p]) => {
        if (cancelled) return;
        setMovie(m);
        if (p && 'positionSec' in p) setResumeFrom(p.positionSec || 0);
      })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [id]);

  async function startPlayback() {
    if (!id) return;
    setError(null);
    try {
      const res = await getPlaybackUrl(id);
      setPlayback(res);
      setPlaying(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start playback');
    }
  }

  if (error) return <div className="text-red-600">{error}</div>;
  if (!movie) return <div className="text-text-secondary">Loading…</div>;

  if (playing && playback) {
    return (
      <div className="-mx-4">
        <div className="aspect-video w-full overflow-hidden rounded-2xl bg-black">
          <HlsOdenPlayer
            mediaId={movie.id}
            src={playback.playbackUrl}
            initialPositionSec={resumeFrom}
            title={movie.title}
            subtitle={movie.year ? String(movie.year) : undefined}
            poster={movie.posterUrl || undefined}
            autoPlay
            theme="system"
          />
        </div>
        <div className="mx-4 mt-4">
          <h1 className="text-xl font-semibold">{movie.title}</h1>
          {movie.overview && <p className="mt-2 text-sm text-text-secondary">{movie.overview}</p>}
        </div>
      </div>
    );
  }

  const ready = movie.status === 'ready';

  return (
    <div className="grid gap-8 md:grid-cols-[260px_1fr]">
      <div className="aspect-[2/3] w-full overflow-hidden rounded-2xl bg-surface-2 shadow-sm">
        {movie.posterUrl ? (
          <img src={movie.posterUrl} alt={movie.title} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-text-secondary text-sm">No poster</div>
        )}
      </div>
      <div>
        <h1 className="text-2xl font-semibold">{movie.title}</h1>
        {movie.year && <div className="mt-1 text-sm text-text-secondary">{movie.year}</div>}
        {movie.overview && <p className="mt-4 max-w-prose text-sm leading-6">{movie.overview}</p>}
        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            onClick={startPlayback}
            disabled={!ready}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-50"
          >
            <Play size={16} fill="currentColor" />
            {ready ? (resumeFrom > 0 ? 'Resume' : 'Play') : 'Not ready'}
          </button>
          {!ready && (
            <span className="text-xs text-text-secondary">
              Status: {movie.status}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
